import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../lib/api'
import { saveOutcome } from '../../lib/equipment'
import { notLiveText } from '../../lib/branchConfig'
import { explain, refusedWith } from '../../lib/staffErrors'
import type { EquipmentDoc, EquipmentProfileRow, ProfileProblem, ProfileUnitKind, UnitSettings } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { SETUP_CHANGED } from '../setupChanged'

/** One equipment profile's document, edited locally and saved whole. */
export function useEquipmentDoc(id: string) {
  const { lang } = useStaffText()
  const [doc, setDoc] = useState<EquipmentDoc | null>(null)
  const [row, setRow] = useState<EquipmentProfileRow | null>(null)
  const [saved, setSaved] = useState('')
  const [revision, setRevision] = useState(0)
  const [kindDefaults, setKindDefaults] = useState<Partial<Record<ProfileUnitKind, UnitSettings>>>({})
  const [problems, setProblems] = useState<ProfileProblem[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [stale, setStale] = useState(false)
  // The answer for a profile no longer selected is dropped.
  const current = useRef(id)
  current.current = id

  const reload = useCallback(async () => {
    const asked = id
    setBusy(true)
    setError('')
    try {
      const a = await api.equipmentDocument(asked)
      if (current.current !== asked) return
      setDoc(a.document)
      setRow(a.equipmentProfile)
      setSaved(JSON.stringify(a.document))
      setRevision(a.revision)
      setKindDefaults(a.kindDefaults ?? {})
      setProblems([])
      setStale(false)
    } catch (err) {
      if (current.current === asked) setError(explain(err, lang, 'common.unreachable'))
    } finally {
      if (current.current === asked) setBusy(false)
    }
  }, [id, lang])

  useEffect(() => {
    setDoc(null)
    setSaved('')
    if (id) void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const dirty = useMemo(() => doc != null && JSON.stringify(doc) !== saved, [doc, saved])

  /** Resolves true when the document went through. */
  const save = useCallback(async (): Promise<boolean> => {
    if (!doc) return false
    const asked = id
    setBusy(true)
    setError('')
    try {
      const r = await api.saveEquipmentDocument(asked, { baseRevision: revision, document: doc })
      if (current.current !== asked) return false
      setRevision(r.revision)
      setSaved(JSON.stringify(doc))
      setProblems(r.warnings ?? [])
      setStale(false)
      setError(notLiveText(r.notLive, lang))
      window.dispatchEvent(new CustomEvent(SETUP_CHANGED))
      return true
    } catch (err) {
      if (current.current !== asked) return false
      const out = saveOutcome(err)
      if (out.kind === 'stale') setStale(true)
      else if (out.kind === 'problems') setProblems(out.problems)
      else setError(refusedWith(err, lang, 'common.unreachable'))
      return false
    } finally {
      if (current.current === asked) setBusy(false)
    }
  }, [doc, id, revision, lang])

  return { doc, row, revision, kindDefaults, dirty, problems, busy, error, stale, setDoc, save, reload }
}
